import { memo, useState } from "react";
import { useEffectOnce } from "react-use";
import Image from "next/image";
import Link from "next/link";
import { LicenceLocalization } from "@/lib/types/Licence";
import { useArticleDetailContext } from "@/src/features/articles/detail/ArticleDetailContext";
import ArticleAuthor from "@/src/features/articles/detail/subs/ArticleAuthor";
import useRandomQuote from "@/src/utils/helpers/useRandomQuote";
import DOMPurify from "isomorphic-dompurify";

const ArticleDetails = memo(() => {
  const { article, documents } = useArticleDetailContext();
  const { getRandomQuote } = useRandomQuote();
  const [randomQuote, setRandomQuote] = useState<LicenceLocalization>();
  const [sanitizedArticleContent, setSanitizedArticleContent] = useState<string>("");

  useEffectOnce(() => {
    const sanitizedContent = DOMPurify.sanitize(article.Content, {
      ADD_ATTR: ["target", "rel"]
    });
    setSanitizedArticleContent(sanitizedContent);
    setRandomQuote(getRandomQuote());
  });

  return (
    <div className="content">
      <div className="post-thumbnail mb--30 position-relative wp-block-image alignwide">
        <figure>
          {
            <Image
              src={article.ImageUrl}
              width={1085}
              height={550}
              priority
              alt={article.ImageAltText}
              style={{
                height: 550,
                objectFit: "cover"
              }}
            />
          }
        </figure>
      </div>
      {randomQuote && (
        <blockquote className="wp-block-quote">
          <p>{randomQuote.Translations}</p>
          <cite>
            <span
              style={{
                color: "var(--color-body)"
              }}
            >
              {randomQuote.DisplayName}
            </span>
          </cite>
        </blockquote>
      )}
      <p dangerouslySetInnerHTML={{ __html: sanitizedArticleContent }}></p>
      {documents && documents.length > 0 && (
        <div className="wp-block-gallery columns-3 is-cropped">
          <ul className="blocks-gallery-grid justify-content-center">
            {documents?.map(document => (
              <li className="blocks-gallery-item" key={document.IdGuid}>
                <figure>
                  <Image className="radius-4" src={document.FileUrl} width={255} height={143} priority alt={document.FileDescription} />
                </figure>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="tagcloud">
        {article.Keywords?.map((keyword, index) => (
          <Link href="#" key={index}>
            {keyword}
          </Link>
        ))}
      </div>
      <div className="about-author">
        <ArticleAuthor />
      </div>
    </div>
  );
});

export default ArticleDetails;
